import type { EmailParty, EmailValue, EmailValueDir } from './EmailTo'

export type PartyEmailDefaultEntry = EmailValueDir | { dir?: EmailValueDir; hide?: boolean }

/** Config shape: modalId -> party display label -> to | cc | bcc | { dir, hide } */
export type EmailModalDefaultsStore = Record<string, Record<string, PartyEmailDefaultEntry>>

export function normalizePartyDefault(entry: PartyEmailDefaultEntry | undefined): {
  dir: EmailValueDir
  hide: boolean
} {
  if (!entry) return { dir: '', hide: false }
  if (typeof entry === 'string') return { dir: entry, hide: false }
  return { dir: entry.dir || '', hide: Boolean(entry.hide) }
}

export function compactPartyDefault({ dir, hide }: { dir: EmailValueDir; hide: boolean }): PartyEmailDefaultEntry {
  if (hide) return { dir, hide: true }
  return dir || ''
}

export function isPartyHiddenInDefaults(
  partyDefaults: Record<string, PartyEmailDefaultEntry> | undefined,
  partyLabel: string,
): boolean {
  if (!partyDefaults) return false
  return normalizePartyDefault(partyDefaults[partyLabel]).hide
}

export function mergeEmailValueWithPartyDefaults(
  rows: EmailParty[],
  partyDefaults: Record<string, PartyEmailDefaultEntry> | undefined,
  current: EmailValue,
  hasUniqueToEmail: boolean,
): EmailValue | null {
  if (!partyDefaults || !Object.keys(partyDefaults).length) return null

  const next: EmailValue = { ...current }
  let hasToAlready = Object.values(current).some((v) => v === 'to')

  for (const row of rows) {
    const { dir, hide } = normalizePartyDefault(partyDefaults[row.party])
    if (hide) continue
    if (!dir || !['to', 'cc', 'bcc'].includes(dir)) continue
    if (current[row.email]) continue
    if (hasUniqueToEmail && dir === 'to') {
      if (hasToAlready) continue
      hasToAlready = true
    }
    next[row.email] = dir
  }

  const keys = new Set([...Object.keys(current), ...Object.keys(next)])
  for (const k of keys) {
    if (k === 'from') continue
    if ((current[k] || '') !== (next[k] || '')) return next
  }
  return null
}
